import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SelectUserRole } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { UserRole, User } from '@/types'
import { userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { PageContainer, PageHeader, LoadingState, FormLayout } from '@/components/layout'
import { NodeRelationshipSelection } from '@/components/nodes/NodeRelationshipSelection'
import { combinedInvitationSchema, CombinedInvitationFormValues } from '@/types/invite'
import { NodeAvatar } from './NodeAvatar'

export interface NodeInviteFormProps<T extends BaseNode> {
  /** Node type for display and routing */
  nodeType: string
  /** Service function to get node by ID */
  getNodeById: (id: string) => Promise<T | null>
  /** Node ID parameter name in URL */
  nodeIdParam?: string
  /** Function to get node ID from URL params */
  getNodeId?: () => string | undefined
  /** Function to check if user can invite members */
  canInviteCheck: (
    node: T,
    currentUser: { id: string; email: string }
  ) => boolean | Promise<boolean>
  /** Function to check if user is already a member */
  isMemberCheck?: (node: T, email: string) => Promise<boolean>
  /** Function to check if user exists and is valid for invitation */
  userExistsCheck?: (node: T, email: string) => Promise<boolean>
  /** Function to add user to node */
  addMember: (node: T, email: string, role: UserRole) => Promise<void>
  /** Function to get role permissions JSX */
  getRolePermissions: (nodeType: string) => React.ReactNode
  /** Custom description for the form */
  description?: string
  /** Custom submit text */
  submitText?: string
  /** Custom cancel text */
  cancelText?: string
}

interface BaseNode {
  id: string
  name: string
  creator?: { id: string }
  users?: { id: string; email: string }[]
  workspace?: { users?: { id: string; email: string }[] }
}

export function NodeInviteForm<T extends BaseNode>({
  nodeType,
  getNodeById,
  nodeIdParam = `${nodeType}Id`,
  getNodeId,
  canInviteCheck,
  isMemberCheck,
  userExistsCheck,
  addMember,
  getRolePermissions,
  description,
  submitText = 'Send Invitation',
  cancelText = 'Back to Members',
}: NodeInviteFormProps<T>) {
  const params = useParams()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [node, setNode] = useState<T | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isInviting, setIsInviting] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<User[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(combinedInvitationSchema),
    defaultValues: {
      mode: 'email',
      email: '',
      role: UserRole.SuperAdmin,
      defaultRole: UserRole.Member,
      selectedUsers: [],
    },
  })

  const watchedValues = watch()
  const mode = watchedValues.mode
  const roleValue = watch('role')
  const defaultRoleValue = watch('defaultRole')
  const selectedUsers = watch('selectedUsers') || []

  const nodeId = getNodeId ? getNodeId() : params?.[nodeIdParam]

  // Load available users who are not current members
  const loadAvailableUsers = useCallback(async () => {
    if (!node) return

    setIsLoadingUsers(true)
    try {
      const allUsers = await userService.getAll()
      const currentMemberEmails = new Set(
        (node.users || []).map((user) => user.email.toLowerCase())
      )

      // Filter out current members and the current user (they can't invite themselves)
      const available = allUsers.filter(
        (user) => !currentMemberEmails.has(user.email.toLowerCase()) && currentUser?.id !== user.id
      )

      setAvailableUsers(available)
    } catch (error) {
      console.error('Error loading available users:', error)
      toast.error('Failed to load available users')
    } finally {
      setIsLoadingUsers(false)
    }
  }, [node, currentUser])

  const loadNode = useCallback(async () => {
    if (!nodeId) {
      toast.error(`${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} ID not found in URL`)
      navigate(`/${nodeType}s`)
      return
    }

    try {
      const data = await getNodeById(nodeId)
      if (data) {
        setNode(data)
      } else {
        toast.error(`${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} not found`)
        navigate(`/${nodeType}s`)
      }
    } catch (error) {
      console.error(`Error loading ${nodeType}:`, error)
      toast.error(`Failed to load ${nodeType}`)
      navigate(`/${nodeType}s`)
    } finally {
      setIsLoading(false)
    }
  }, [nodeId, nodeType, getNodeById, navigate])

  useEffect(() => {
    loadNode()
  }, [loadNode])

  useEffect(() => {
    if (node) {
      loadAvailableUsers()
    }
  }, [node, loadAvailableUsers])

  const handleCancel = useCallback(() => {
    if (node) {
      navigate(`/${nodeType}s/${node.id}/members`)
    }
  }, [node, nodeType, navigate])

  // Check permissions on mount
  const checkPermissions = useCallback(async () => {
    if (canInviteCheck && node && currentUser) {
      try {
        const canInvite = await canInviteCheck(node, currentUser)
        if (!canInvite) {
          toast.error(`You don't have permission to invite members to this ${nodeType}`)
          handleCancel()
        }
      } catch {
        toast.error(`Failed to check permissions`)
        handleCancel()
      }
    }
  }, [canInviteCheck, node, currentUser, nodeType, handleCancel])

  // Run permission check
  useEffect(() => {
    checkPermissions()
  }, [checkPermissions])

  const inviteIsMemberCheck = useCallback(
    async (email: string) => {
      if (!node || !isMemberCheck) return false
      return await isMemberCheck(node, email)
    },
    [node, isMemberCheck]
  )

  const inviteUserExistsCheck = useCallback(
    async (email: string) => {
      if (!node || !userExistsCheck) {
        // Default: any existing user
        const users = await userService.getAll()
        return users.some((u) => u.email.toLowerCase() === email.toLowerCase())
      }
      return await userExistsCheck(node, email)
    },
    [node, userExistsCheck]
  )

  const inviteAddMember = useCallback(
    async (email: string, role: UserRole) => {
      if (!node) throw new Error(`${nodeType} not found`)
      await addMember(node, email, role)
    },
    [node, addMember, nodeType]
  )

  const handleSuccess = useCallback(() => {
    if (node) {
      navigate(`/${nodeType}s/${node.id}/members`)
    }
  }, [node, nodeType, navigate])

  const onSubmit = async (data: CombinedInvitationFormValues) => {
    setIsInviting(true)
    try {
      if (data.mode === 'email') {
        // Handle email invitation (existing logic)
        if (!data.email || !data.role) {
          toast.error('Email and role are required')
          setIsInviting(false)
          return
        }

        const userExists = await inviteUserExistsCheck(data.email)

        // Check if user is already a member (regardless of whether they exist in system)
        if (isMemberCheck) {
          const isAlreadyMember = await inviteIsMemberCheck(data.email)
          if (isAlreadyMember) {
            toast.error('This user is already a member')
            setIsInviting(false)
            return
          }
        }

        // Add user to node
        await inviteAddMember(data.email, data.role as UserRole)

        // Show appropriate success message based on whether user exists
        if (userExists) {
          toast.success('Existing user invited successfully')
        } else {
          toast.success('Invitation sent successfully - user will receive an email to join')
        }
        reset()
        handleSuccess()
      } else {
        // Handle user selection invitation
        if (!data.selectedUsers || data.selectedUsers.length === 0) {
          toast.error('Please select at least one user')
          setIsInviting(false)
          return
        }

        // Add all selected users to node
        const promises = data.selectedUsers.map((user) =>
          inviteAddMember(user.email, user.role as UserRole)
        )

        await Promise.all(promises)
        toast.success(
          `${data.selectedUsers.length} member${data.selectedUsers.length > 1 ? 's' : ''} added successfully`
        )
        reset()
        handleSuccess()
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to invite member')
    } finally {
      setIsInviting(false)
    }
  }

  if (isLoading) {
    return (
      <PageContainer maxWidth="2xl">
        <LoadingState text={`Loading ${nodeType}...`} />
      </PageContainer>
    )
  }

  if (!nodeId || !node) {
    return (
      <PageContainer maxWidth="2xl">
        <div className="text-center text-muted-foreground">
          <p>{nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} not found</p>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="2xl">
      <PageHeader title={`Invite Member to ${node.name}`} />

      <FormLayout
        title="Member Details"
        description={
          description ||
          `Invite a user to join this ${nodeType} by email or select from existing users`
        }
        onSubmit={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        submitText={isInviting ? 'Inviting...' : submitText}
        cancelText={cancelText}
        loading={isInviting}
      >
        <Tabs
          value={mode}
          onValueChange={(value) => setValue('mode', value as 'email' | 'selection')}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email">Invite by Email</TabsTrigger>
            <TabsTrigger value="selection">Add Existing Users</TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                {...register('email')}
                aria-invalid={errors.email ? 'true' : 'false'}
                disabled={isInviting}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{String(errors.email.message)}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {nodeType === 'project'
                  ? "The user must be a member of the workspace. If they don't have an account, they'll receive an invitation email."
                  : "If the user doesn't have an account, they'll receive an invitation email to join."}
              </p>
            </div>

            <SelectUserRole
              value={roleValue}
              onValueChange={(value) => setValue('role', value)}
              label="Role"
              placeholder="Select role"
              error={errors.role ? String(errors.role.message) : undefined}
              showLabel={true}
              disabled={isInviting}
            />
          </TabsContent>

          <TabsContent value="selection" className="space-y-4">
            <div className="space-y-2">
              <Label>
                Default Role for Selected Users <span className="text-destructive">*</span>
              </Label>
              <SelectUserRole
                value={defaultRoleValue}
                onValueChange={(value) => setValue('defaultRole', value)}
                label=""
                placeholder="Select default role"
                showLabel={false}
                disabled={isInviting}
              />
              <p className="text-xs text-muted-foreground">
                You can change individual roles for each selected user below
              </p>
            </div>

            <NodeRelationshipSelection
              mode="multi"
              availableNodes={availableUsers}
              loading={isLoadingUsers}
              label="Select Users"
              placeholder="Search for users to add..."
              selectedNodes={selectedUsers}
              onAddNode={(user) => {
                const currentUsers = selectedUsers || []
                const userWithRole = { ...user, role: defaultRoleValue as UserRole }
                setValue('selectedUsers', [...currentUsers, userWithRole])
              }}
              onRemoveNode={(userId) => {
                const currentUsers = selectedUsers || []
                setValue(
                  'selectedUsers',
                  currentUsers.filter((u) => u.id !== userId)
                )
              }}
              maxSelections={10}
              renderNode={(user, size = 'sm') => <NodeAvatar node={user as User} size={size} />}
              getNodeDescription={(user) => user.email}
              getInputValue={(user) => `${user.name} (${user.email})`}
              currentUser={currentUser as User}
            />

            {errors.selectedUsers && (
              <p className="text-sm text-destructive">{String(errors.selectedUsers.message)}</p>
            )}

            {selectedUsers.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Users and Roles</Label>
                <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                  {selectedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 bg-background rounded border"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                      <SelectUserRole
                        value={user.role}
                        onValueChange={(value) => {
                          const currentUsers = selectedUsers || []
                          const updatedUsers = currentUsers.map((u) =>
                            u.id === user.id ? { ...u, role: value as UserRole } : u
                          )
                          setValue('selectedUsers', updatedUsers)
                        }}
                        label=""
                        placeholder="Select role"
                        showLabel={false}
                        disabled={isInviting}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {getRolePermissions && (
          <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
            {getRolePermissions(nodeType)}
          </div>
        )}
      </FormLayout>
    </PageContainer>
  )
}
